package com.schoolconnect.app.repository.user

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.UserCreateRequest
import com.schoolconnect.app.datasource.network.request.UserSchoolRoleUpdateRequest
import com.schoolconnect.app.datasource.network.request.UserUpdateRequest
import com.schoolconnect.app.datasource.network.response.PlatformUserResponse
import com.schoolconnect.app.datasource.network.response.UserRoleAuthorizationsResponse
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.util.scLog
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

object UserRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] UserRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private data class UsersCacheKey(
        val query: String,
        val schoolId: String?,
        val roleIds: List<Int>,
        val isActive: Boolean?,
        val createdFrom: String,
        val createdTo: String
    )

    private data class CachedUsers(
        val key: UsersCacheKey,
        val users: List<PlatformUserResponse>
    )

    private const val MAX_MEMORY_CACHE_ENTRIES = 32
    private val cachedUsers = LinkedHashMap<UsersCacheKey, CachedUsers>()
    private val cachedDisplayNames = LinkedHashMap<String, String>()

    suspend fun loadUsers(
        query: String = "",
        schoolId: String? = null,
        roleIds: List<Int> = emptyList(),
        isActive: Boolean? = null,
        createdFrom: String = "",
        createdTo: String = "",
        forceRemote: Boolean = false
    ): Result<List<PlatformUserResponse>> {
        val normalizedQuery = query.trim()
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedRoleIds = roleIds.distinct().sorted()
        val normalizedCreatedFrom = createdFrom.trim()
        val normalizedCreatedTo = createdTo.trim()
        val cacheKey = UsersCacheKey(
            query = normalizedQuery,
            schoolId = normalizedSchoolId,
            roleIds = normalizedRoleIds,
            isActive = isActive,
            createdFrom = normalizedCreatedFrom,
            createdTo = normalizedCreatedTo
        )

        if (!forceRemote) {
            cachedUsers.remove(cacheKey)?.let { cache ->
                cachedUsers[cacheKey] = cache
                scLog(
                    "$TAG loadUsers cacheHit q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} count=${cache.users.size}"
                )
                return Result.success(cache.users)
            }
        }

        scLog(
            "$TAG loadUsers start q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} forceRemote=$forceRemote"
        )
        return runCatching {
            val users = loadUsersPage(
                roleIds = normalizedRoleIds,
                query = normalizedQuery,
                schoolId = normalizedSchoolId,
                isActive = isActive,
                createdFrom = normalizedCreatedFrom,
                createdTo = normalizedCreatedTo
            )
            val dedupedUsers = users.distinctBy { it.id }
            if (cachedUsers.size >= MAX_MEMORY_CACHE_ENTRIES) {
                cachedUsers.entries.firstOrNull()?.key?.let(cachedUsers::remove)
            }
            cachedUsers[cacheKey] = CachedUsers(cacheKey, dedupedUsers)
            scLog("$TAG loadUsers complete count=${dedupedUsers.size}")
            dedupedUsers
        }.onFailure { throwable ->
            scLog("$TAG loadUsers failure q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"}", throwable)
        }
    }

    fun invalidateCache() {
        cachedUsers.clear()
        cachedDisplayNames.clear()
    }

    suspend fun createUser(request: UserCreateRequest): Result<Unit> {
        scLog(
            "$TAG createUser start fullName=${request.fullName.ifBlank { "<blank>" }} roleId=${request.roleId} schoolId=${request.schoolId.orEmpty().ifBlank { "<blank>" }}"
        )
        return runCatching {
            KtorApiClient.createUser(request).getOrElse { throw it }
            invalidateCache()
            scLog("$TAG createUser complete fullName=${request.fullName.ifBlank { "<blank>" }}")
        }
    }

    suspend fun updateUser(userId: String, request: UserUpdateRequest): Result<Unit> {
        val normalizedUserId = userId.trim()
        scLog("$TAG updateUser start userId=$normalizedUserId")
        return runCatching {
            KtorApiClient.updateUser(normalizedUserId, request).getOrElse { throw it }
            invalidateCache()
            scLog("$TAG updateUser complete userId=$normalizedUserId")
        }
    }

    suspend fun toggleUserStatus(userId: String, isActive: Boolean): Result<Unit> {
        val normalizedUserId = userId.trim()
        scLog("$TAG toggleUserStatus start userId=$normalizedUserId isActive=$isActive")
        return runCatching {
            KtorApiClient.toggleUserStatus(normalizedUserId, isActive).getOrElse { throw it }
            invalidateCache()
            scLog("$TAG toggleUserStatus complete userId=$normalizedUserId isActive=$isActive")
        }
    }

    suspend fun deleteUser(userId: String): Result<Unit> {
        val normalizedUserId = userId.trim()
        scLog("$TAG deleteUser start userId=$normalizedUserId")
        return runCatching {
            KtorApiClient.deleteUser(normalizedUserId).getOrElse { throw it }
            invalidateCache()
            scLog("$TAG deleteUser complete userId=$normalizedUserId")
        }
    }

    suspend fun updateUserRoleAssignment(
        assignmentId: String,
        request: UserSchoolRoleUpdateRequest
    ): Result<Unit> {
        val normalizedAssignmentId = assignmentId.trim()
        scLog("$TAG updateUserRoleAssignment start assignmentId=$normalizedAssignmentId")
        return runCatching {
            KtorApiClient.updateUserRoleAssignment(normalizedAssignmentId, request).getOrElse { throw it }
            invalidateCache()
            scLog("$TAG updateUserRoleAssignment complete assignmentId=$normalizedAssignmentId")
        }
    }

    suspend fun updateUserRoleSelection(assignmentId: String): Result<Unit> {
        val normalizedAssignmentId = assignmentId.trim()
        if (normalizedAssignmentId.isBlank()) {
            return Result.failure(IllegalArgumentException("assignmentId is required"))
        }
        scLog("$TAG updateUserRoleSelection start assignmentId=$normalizedAssignmentId")
        return runCatching {
            KtorApiClient.updateUserRoleSelection(normalizedAssignmentId).getOrElse { throw it }
            scLog("$TAG updateUserRoleSelection complete assignmentId=$normalizedAssignmentId")
        }
    }

    suspend fun loadUserRoleAuthorizations(userId: String): Result<UserRoleAuthorizationsResponse> {
        val normalizedUserId = userId.trim()
        if (normalizedUserId.isBlank()) {
            return Result.failure(IllegalArgumentException("userId is required"))
        }
        scLog("$TAG loadUserRoleAuthorizations start userId=$normalizedUserId")
        return KtorApiClient.getUserRoleAuthorizations(normalizedUserId)
            .onSuccess { response ->
                scLog(
                    "$TAG loadUserRoleAuthorizations complete userId=$normalizedUserId count=${response.authorizations.orEmpty().size}"
                )
            }
            .onFailure { throwable ->
                scLog("$TAG loadUserRoleAuthorizations failure userId=$normalizedUserId", throwable)
            }
    }

    suspend fun loadUserProfileRaw(userId: String): Result<JsonObject> {
        val normalizedUserId = userId.trim()
        scLog("$TAG loadUserProfileRaw start userId=$normalizedUserId")
        return runCatching {
            val raw = KtorApiClient.getUserProfileRaw(normalizedUserId).getOrElse { throw it }
            scLog("$TAG loadUserProfileRaw complete userId=$normalizedUserId")
            raw
        }
    }

    suspend fun loadUserDisplayName(userId: String): Result<String> {
        val normalizedUserId = userId.trim()
        if (normalizedUserId.isBlank()) return Result.success("")
        cachedDisplayNames[normalizedUserId]?.let { return Result.success(it) }

        return loadUserProfileRaw(normalizedUserId).map { raw ->
            val displayName = raw.firstNonBlankString(
                "full_name", "name", "display_name", "fullName", "displayName"
            ).ifBlank {
                listOf(
                    raw.firstNonBlankString("first_name", "firstName"),
                    raw.firstNonBlankString("middle_name", "middleName"),
                    raw.firstNonBlankString("last_name", "lastName")
                ).filter { it.isNotBlank() }.joinToString(" ")
            }.trim()
            if (displayName.isNotBlank()) {
                if (cachedDisplayNames.size >= MAX_MEMORY_CACHE_ENTRIES) {
                    cachedDisplayNames.entries.firstOrNull()?.key?.let(cachedDisplayNames::remove)
                }
                cachedDisplayNames[normalizedUserId] = displayName
            }
            displayName
        }
    }

    suspend fun loadAssignmentId(userId: String): Result<String> {
        return loadUserProfileRaw(userId).mapCatching { raw -> raw.extractAssignmentId() }
    }

    private suspend fun loadUsersPage(
        roleIds: List<Int>,
        query: String,
        schoolId: String?,
        isActive: Boolean?,
        createdFrom: String,
        createdTo: String
    ): List<PlatformUserResponse> {
        val normalizedRoleIds = roleIds.distinct().sorted()
        return loadPagedItems(
            tag = TAG,
            operation = "loadUsersPage",
            pageSize = PAGE_SIZE,
            maxPages = MAX_PAGES,
            startContext = "roleIds=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }}"
        ) { offset ->
            val response = KtorApiClient.getUsers(
                q = query.ifBlank { null },
                schoolId = schoolId,
                roleIds = normalizedRoleIds,
                isActive = isActive,
                createdFrom = createdFrom.ifBlank { null },
                createdTo = createdTo.ifBlank { null },
                limit = PAGE_SIZE,
                offset = offset
            ).getOrElse { throwable ->
                scLog("$TAG loadUsersPage failed roleIds=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} offset=$offset", throwable)
                throw throwable
            }

            val page = response.users.orEmpty()
            scLog("$TAG loadUsersPage page roleIds=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} offset=$offset count=${page.size}")
            page
        }
    }

    private fun JsonObject.extractAssignmentId(): String {
        val nestedAssignment = this["assignment"]?.jsonObject
        val topLevelAssignmentId = this["assignment_id"]?.jsonPrimitive?.contentOrNull.orEmpty()
        val nestedAssignmentId = nestedAssignment?.get("assignment_id")?.jsonPrimitive?.contentOrNull.orEmpty()
        return topLevelAssignmentId.ifBlank { nestedAssignmentId }
    }

    private fun JsonObject.firstNonBlankString(vararg keys: String): String {
        val directValue = keys.asSequence()
            .map { key -> this[key]?.jsonPrimitive?.contentOrNull.orEmpty().trim() }
            .firstOrNull { it.isNotBlank() }
            .orEmpty()
        if (directValue.isNotBlank()) return directValue

        // The detailed user endpoint may wrap the role profile under one of
        // these protocol objects. Keep the traversal bounded to known profile
        // containers so a school name or unrelated nested label is never used.
        val profileContainers = listOf("profile", "user", "app_staff", "school_staff", "parent", "data")
        return profileContainers.asSequence()
            .mapNotNull { key -> this[key]?.jsonObject }
            .map { nested -> nested.firstNonBlankString(*keys) }
            .firstOrNull { it.isNotBlank() }
            .orEmpty()
    }
}
